import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import Header from "../Header/header";
import Fotter from "../Fotter/fotter";
import API_URL from "../../config";

import {
  Search,
  MapPin,
  Clock,
  Calendar,
  TrendingUp,
  ArrowRight,
  Filter,
  Compass,
  Star,
  Sparkles,
  Zap,
  ShieldCheck,
  Coins,
  Loader2,
  List
} from "lucide-react";

function TravelPackagesUserPage() {
  const [travelPackages, setTravelPackages] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [filterLocation, setFilterLocation] = useState("All");
  const [priceRange, setPriceRange] = useState([0, 1000000]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const { user } = useAuth();
  const navigate = useNavigate();

  const sriLankaCities = [
    "All", "Colombo", "Kandy", "Galle", "Nuwara Eliya", "Sigiriya", "Ella", "Polonnaruwa", "Trincomalee", "Hikkaduwa", "Mirissa"
  ];

  useEffect(() => {
    const fetchTravelPackages = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/travelpackage`);
        const data = await response.json();
        if (data.packages) {
          setTravelPackages(data.packages);
        }
      } catch (err) {
        console.error("Error fetching travel packages:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTravelPackages();
  }, []);

  const filteredPackages = travelPackages.filter((pkg) => {
    const matchesSearch =
      pkg.packageName.toLowerCase().includes(search.toLowerCase()) ||
      pkg.packageDescription.toLowerCase().includes(search.toLowerCase()) ||
      pkg.destinations.some((d) => d.toLowerCase().includes(search.toLowerCase()));

    const matchesType = filterType === "All" || pkg.packageType.toLowerCase() === filterType.toLowerCase();
    const matchesLocation = filterLocation === "All" || pkg.destinations.some((d) => d.toLowerCase() === filterLocation.toLowerCase());
    const matchesPrice = pkg.price >= priceRange[0] && pkg.price <= priceRange[1];

    const pkgStart = new Date(pkg.startDate);
    const pkgEnd = new Date(pkg.endDate);
    const matchesDate = (!fromDate || pkgStart >= new Date(fromDate)) && (!toDate || pkgEnd <= new Date(toDate));

    return matchesSearch && matchesType && matchesLocation && matchesPrice && matchesDate;
  });

  const handleBookNow = (pkg) => {
    if (!pkg.availability) return;
    if (!user) {
      toast.info("Unlock premium member pricing. Please login to continue.", {
        icon: <ShieldCheck className="text-sky-500" />
      });
      navigate("/login");
      return;
    }
    navigate("/booking-form", { state: pkg });
  };

  return (
    <div className="bg-white text-sky-950 font-sans selection:bg-sky-100 selection:text-sky-900">
      <Header />

      {/* Modern Hero Section */}
      <section className="relative min-h-[75vh] flex items-center justify-center flex-col">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1528666578051-ccb79529712a?q=80&w=2000&auto=format&fit=crop"
            className="w-full h-full object-cover scale-105 animate-slow-zoom"
            alt="Hero Background"
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1200&q=80"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-sky-950/50 via-sky-950/30 to-transparent" />
        </div>

        <div className="relative z-30 max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 text-white text-[10px] font-black uppercase tracking-[0.3em] mb-8 animate-fade-in shadow-2xl">
            <Sparkles size={12} className="text-sky-300" />
            Bespoke Expeditions 2026
          </div>
          <h1 className="text-6xl md:text-8xl font-black text-white mb-8 drop-shadow-2xl animate-slide-up leading-none tracking-tighter">
            Your Island Adventure Awaits
          </h1>
          <p className="text-lg md:text-2xl text-white/80 font-medium mb-16 max-w-3xl mx-auto drop-shadow-md animate-slide-up-delayed leading-relaxed">
            Uncover the secret rhythms of the Pearl of the Indian Ocean through our curated sensory expeditions.
          </p>

          {/* Quick Search Bar */}
          <div className="max-w-4xl mx-auto bg-white/95 backdrop-blur-xl p-3 rounded-[2.5rem] shadow-2xl flex flex-col lg:flex-row items-center gap-3 border border-white/20 relative z-30">
            <div className="flex-grow flex items-center gap-4 pl-8 w-full py-4 lg:py-0">
              <Search size={22} className="text-sky-400" />
              <input
                type="text"
                placeholder="Search by destination or experience..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent outline-none font-bold placeholder:text-gray-300 text-sky-950 text-lg"
              />
            </div>
            <div className="h-12 w-px bg-gray-100 hidden lg:block" />
            <div className="flex items-center gap-4 px-6 py-4 lg:py-0 w-full lg:w-auto">
              <MapPin size={20} className="text-rose-400" />
              <select
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                className="bg-transparent font-black text-[10px] uppercase tracking-widest text-sky-900 outline-none cursor-pointer"
              >
                {sriLankaCities.map(city => <option key={city} value={city}>{city}</option>)}
              </select>
            </div>
            <button className="w-full lg:w-auto px-12 py-5 bg-sky-950 text-white font-black rounded-[2rem] hover:bg-sky-800 transition-all flex items-center justify-center gap-3 shadow-xl active:scale-95 group">
              <Compass size={20} className="group-hover:rotate-12 transition-transform" />
              DISCOVER
            </button>
          </div>
        </div>
      </section>

      {/* Discovery Hub */}
      <section className="max-w-7xl mx-auto px-6 relative z-20 pb-32">
        <div className="bg-white rounded-[4rem] shadow-2xl border border-gray-50 p-10 md:p-16 overflow-hidden">

          <div className="flex flex-col lg:flex-row gap-16">
            {/* Left Column: Refined Filters */}
            <aside className="lg:w-80 space-y-12">
              <div className="sticky top-10">
                <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-sky-900/40 mb-10 flex items-center gap-3">
                  <Filter size={16} className="text-sky-500" />
                  Experience Filters
                </h3>

                <div className="space-y-10">
                  {/* Price Scale */}
                  <div className="space-y-5">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-black tracking-widest uppercase text-sky-950">Investment</label>
                      <span className="text-[9px] font-black text-sky-600 bg-sky-50 px-3 py-1 rounded-full">
                        {priceRange[1].toLocaleString()} LKR
                      </span>
                    </div>
                    <div className="relative pt-2">
                      <input
                        type="range"
                        min="0"
                        max="1000000"
                        step="10000"
                        value={priceRange[1]}
                        onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                        className="w-full h-1.5 bg-gray-100 rounded-full appearance-none cursor-pointer accent-sky-600 hover:accent-sky-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* Vibe Selection */}
                  <div className="space-y-5">
                    <label className="text-[11px] font-black tracking-widest uppercase text-sky-950">Curated Vibe</label>
                    <div className="grid grid-cols-2 gap-3">
                      {["All", "Adventure", "Leisure", "Cultural", "Wildlife"].map(type => (
                        <button
                          key={type}
                          onClick={() => setFilterType(type)}
                          className={`
                            px-4 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all
                            ${filterType === type
                              ? "bg-sky-950 text-white shadow-2xl shadow-sky-950/20 ring-4 ring-sky-500/10"
                              : "bg-gray-50 text-gray-400 hover:bg-white hover:shadow-lg border border-transparent hover:border-gray-100"}
                          `}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Window of Opportunity */}
                  <div className="space-y-5">
                    <label className="text-[11px] font-black tracking-widest uppercase text-sky-950 flex items-center gap-2">
                      <Calendar size={14} className="text-amber-500" />
                      Travel Window
                    </label>
                    <div className="space-y-3">
                      <div className="relative group">
                        <input
                          type="date"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                          className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl text-[10px] font-black text-sky-950 outline-none focus:border-sky-500 focus:bg-white transition-all appearance-none"
                        />
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-300">
                          <Clock size={16} />
                        </div>
                      </div>
                      <div className="relative group">
                        <input
                          type="date"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                          className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl text-[10px] font-black text-sky-950 outline-none focus:border-sky-500 focus:bg-white transition-all appearance-none"
                        />
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-300">
                          <Clock size={16} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Membership Teaser */}
                <div className="mt-12 bg-sky-950 rounded-[2.5rem] p-10 text-white relative overflow-hidden group/membership shadow-2xl shadow-sky-950/20">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-sky-500/20 rounded-full blur-3xl -mr-16 -mt-16 group-hover/membership:scale-150 transition-transform duration-1000" />
                  <Zap className="text-sky-400 mb-6" size={32} />
                  <h4 className="text-2xl font-black mb-3 italic tracking-tight underline-offset-4 decoration-sky-500/20 underline">Elite Discovery</h4>
                  <p className="text-[10px] font-bold text-white/50 leading-relaxed mb-8 uppercase tracking-widest uppercase">
                    Unlock 15% Member Benefit on your first major expedition.
                  </p>
                  <button className="w-full py-4 bg-white text-sky-950 text-[9px] font-black uppercase tracking-widest rounded-xl hover:translate-y-[-2px] transition-all shadow-xl active:scale-95">
                    Access Tunnel
                  </button>
                </div>
              </div>
            </aside>

            {/* Right Column: Experience Feed */}
            <div className="flex-grow">
              <div className="flex flex-col md:flex-row items-end justify-between mb-16 border-b border-gray-50 pb-12 gap-8">
                <div>
                  <span className="text-sky-500 font-black uppercase text-[10px] tracking-[0.3em] mb-4 inline-block">Bespoke Collection</span>
                  <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-sky-950 leading-none">Experience <span className="text-sky-600 italic">Stream</span></h2>
                  <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.2em] mt-6">Indexing {filteredPackages.length} Narrative Journeys Across the Island</p>
                </div>
                <div className="flex gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center text-sky-900 shadow-sm border border-gray-100 hover:bg-sky-50 cursor-pointer transition-colors"><List size={20} /></div>
                  <div className="w-14 h-14 rounded-2xl bg-sky-950 flex items-center justify-center text-white shadow-2xl shadow-sky-950/20"><Compass size={20} /></div>
                </div>
              </div>

              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-40 gap-6">
                  <Loader2 className="animate-spin text-sky-200" size={64} strokeWidth={1} />
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-300">Synchronizing Master Registry...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  {filteredPackages.length > 0 ? (
                    filteredPackages.map((pkg, idx) => (
                      <div
                        key={pkg._id}
                        className="group flex flex-col h-full animate-fade-in"
                        style={{ animationDelay: `${idx * 0.1}s` }}
                      >
                        {/* Visual Card */}
                        <div className="relative h-[30rem] rounded-[3.5rem] overflow-hidden shadow-sm group-hover:shadow-2xl group-hover:-translate-y-2 transition-all duration-700">
                          <img
                            src={pkg.travelImages?.[0] || "https://images.unsplash.com/photo-1544644181-1484b3fdfc62?q=80&w=1000&auto=format&fit=crop"}
                            alt={pkg.packageName}
                            className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-110"
                            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1544644181-1484b3fdfc62?q=80&w=1000&auto=format&fit=crop"; }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-sky-950/95 via-sky-950/20 to-transparent" />

                          <div className="absolute top-10 left-10 flex flex-col gap-3">
                            <span className="px-4 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-[9px] font-black text-white uppercase tracking-widest border border-white/20 w-fit">
                              {pkg.packageType}
                            </span>
                          </div>

                          <div className="absolute top-10 right-10">
                            <button className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex items-center justify-center text-white hover:bg-sky-500 hover:border-sky-500 transition-all shadow-xl group/fav">
                              <Star size={20} className="group-hover/fav:fill-white transition-all" />
                            </button>
                          </div>

                          <div className="absolute bottom-10 left-10 right-10 text-white">
                            <div className="flex items-center gap-3 text-sky-300 mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 translate-y-4 group-hover:translate-y-0">
                              <TrendingUp size={14} />
                              <span className="text-[9px] font-black uppercase tracking-[0.4em]">Elite Path</span>
                            </div>
                            <h3 className="text-3xl font-black tracking-tight leading-tight mb-8 group-hover:text-sky-300 transition-colors drop-shadow-2xl">{pkg.packageName}</h3>

                            <div className="flex items-center justify-between border-t border-white/10 pt-8 mt-2">
                              <div className="flex items-center gap-6">
                                <div className="flex flex-col">
                                  <span className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-1">Hub</span>
                                  <span className="text-xs font-black flex items-center gap-2 saturate-0 group-hover:saturate-100 transition-all"><MapPin size={12} className="text-rose-400" /> {pkg.destinations[0]}</span>
                                </div>
                                <div className="w-px h-8 bg-white/10" />
                                <div className="flex flex-col">
                                  <span className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-1">Tempo</span>
                                  <span className="text-xs font-black flex items-center gap-2"><Clock size={12} className="text-sky-300" /> {pkg.duration}</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-1 block">Starts From</span>
                                <span className="text-2xl font-black leading-none">{pkg.price.toLocaleString()} <span className="text-[10px] text-sky-400">{pkg.currency}</span></span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Narrative Snippet */}
                        <div className="pt-10 px-6 flex flex-col flex-grow">
                          <p className="text-gray-400 font-serif text-lg leading-relaxed line-clamp-2 mb-10 italic opacity-80">
                            "{pkg.packageDescription}"
                          </p>
                          <button
                            onClick={() => handleBookNow(pkg)}
                            disabled={!pkg.availability}
                            className={`
                                w-full py-5 rounded-[2rem] text-[10px] font-black uppercase tracking-[0.3em] flex items-center justify-center gap-4 transition-all shadow-xl active:scale-95 group/btn
                                ${pkg.availability
                                ? "bg-sky-950 text-white hover:bg-sky-800 shadow-sky-950/20"
                                : "bg-gray-100 text-gray-400 cursor-not-allowed"}
                            `}
                          >
                            {pkg.availability ? (
                              <>
                                Secure Reservation
                                <ArrowRight size={18} className="group-hover/btn:translate-x-3 transition-transform duration-500" />
                              </>
                            ) : "Reserved Registry"}
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full py-40 flex flex-col items-center justify-center bg-gray-50/50 rounded-[4rem] border-2 border-dashed border-gray-100 gap-8">
                      <div className="w-28 h-28 bg-white rounded-full shadow-inner flex items-center justify-center text-gray-200">
                        <Compass size={56} strokeWidth={1} />
                      </div>
                      <div className="text-center max-w-sm">
                        <h3 className="text-2xl font-black text-sky-950 italic tracking-tight">The Horizon is Empty</h3>
                        <p className="text-[11px] text-gray-400 font-bold mt-4 leading-relaxed uppercase tracking-widest">Our expedition chroniclers haven't documented this specific path yet.</p>
                        <button
                          onClick={() => { setSearch(""); setFilterType("All"); setFilterLocation("All"); setPriceRange([0, 1000000]); }}
                          className="mt-12 px-10 py-4 bg-sky-950 text-white rounded-2xl text-[9px] font-black uppercase tracking-[0.3em] shadow-2xl shadow-sky-950/20 active:scale-95"
                        >
                          Reset Discovery Hub
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Partnerships */}
      <section className="bg-sky-50/50 py-24 mb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-end justify-between gap-8 mb-16">
            <div className="max-w-xl">
              <span className="text-sky-500 font-black uppercase text-[10px] tracking-widest mb-4 inline-block">Global Standards</span>
              <h2 className="text-4xl font-black text-sky-950 tracking-tight leading-none mb-6">Why Travelers Choose <span className="text-sky-600 italic">TravelAura Elite</span></h2>
              <p className="text-sm text-gray-400 font-bold">We partner with local artisans and sustainable luxury providers to ensure every journey is meaningful.</p>
            </div>
            <div className="flex gap-4">
              <div className="p-6 bg-white rounded-3xl shadow-sm border border-gray-100 text-center flex flex-col items-center gap-2">
                <ShieldCheck size={28} className="text-emerald-500" />
                <span className="text-[10px] font-black uppercase tracking-widest">Secure Payments</span>
              </div>
              <div className="p-6 bg-white rounded-3xl shadow-sm border border-gray-100 text-center flex flex-col items-center gap-2">
                <Coins size={28} className="text-amber-500" />
                <span className="text-[10px] font-black uppercase tracking-widest">Best Price Lock</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 opacity-20 hover:opacity-100 transition-opacity">
            <div className="flex items-center justify-center grayscale font-black text-2xl tracking-tighter italic text-sky-900/40">SRILANKAN</div>
            <div className="flex items-center justify-center grayscale font-black text-2xl tracking-tighter">LUXURY TRAILS</div>
            <div className="flex items-center justify-center grayscale font-black text-2xl tracking-tighter">CEYLON ECO</div>
            <div className="flex items-center justify-center grayscale font-black text-2xl tracking-tighter">SKY HORIZON</div>
          </div>
        </div>
      </section>

      <Fotter />

      {/* Embedded Animations */}
      <style>{`
        @keyframes slow-zoom {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }
        .animate-slow-zoom { animation: slow-zoom 20s ease-in-out infinite alternate; }
        
        .animate-fade-in { animation: fadeIn 0.8s ease-out forwards; }
        .animate-slide-up { animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-slide-up-delayed { animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards 0.2s; opacity: 0; }
        .animate-slide-up-more { animation: slideUp 1s cubic-bezier(0.16, 1, 0.3, 1) forwards 0.4s; opacity: 0; }
        .animate-spin-slow { animation: spin 4s linear infinite; }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { 
          from { opacity: 0; transform: translateY(30px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default TravelPackagesUserPage;